import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { NoteTarget } from '@shared/flow/notes';
import { accountInCommandCenter, contactInCommandCenter } from '../../../../../../../mini-apps/_shared/flow-links';
import { GlobalSearch } from '../../../search/GlobalSearch';
import { RAIL_PANE_GAP } from '../rail/RailSearchRow';
import { useFlowNotesStore } from './flow-notes-store';

const LINK_CLASS = 'text-xs font-semibold text-content-action underline';

/**
 * The shell's record search (`GlobalSearch`, never a second search) wired to
 * the Notes store: picking an account or contact opens its thread, and the
 * contacts opened this way feed the search's own "Recent" section.
 */
export function NotesRecordSearch({ activeEntity }: { activeEntity: 'account' | 'contact' }) {
  const recents = useFlowNotesStore(s => s.recents);
  const contactRows = useFlowNotesStore(s => s.contactRows);
  const recentContacts = useMemo(
    () =>
      recents.flatMap(recent => {
        const row = recent.kind === 'contact' ? contactRows[recent.id] : undefined;
        return row ? [row] : [];
      }),
    [recents, contactRows],
  );

  return (
    <GlobalSearch
      mode="all"
      fullWidth
      activeEntity={activeEntity}
      recentContacts={recentContacts}
      onSelectAccount={accountId => useFlowNotesStore.getState().openTarget({ kind: 'account', id: accountId, accountId })}
      onSelectContact={(contactId, accountId, row) => {
        const store = useFlowNotesStore.getState();
        store.rememberContactRow(row);
        store.openTarget({ kind: 'contact', id: contactId, accountId });
      }}
    />
  );
}

export interface NotesHeaderProps {
  target: NoteTarget;
  /** The open record's name once the thread loaded. */
  recordName: string | null;
  /** The People filter, shown on account threads only (the pane decides). */
  filterSlot?: ReactNode;
}

/** Notes pane header: record search, the open record's name, and links to its page. */
export function NotesHeader({ target, recordName, filterSlot }: NotesHeaderProps) {
  const accountId = target.accountId;
  return (
    <div className={`flex flex-col ${RAIL_PANE_GAP}`}>
      <NotesRecordSearch activeEntity={target.kind} />
      <div className="flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate text-sm font-semibold text-content-primary">
          {recordName ? `Notes · ${recordName}` : 'Notes'}
        </h2>
        {filterSlot}
      </div>
      <div className="flex items-center gap-3">
        {accountId && (
          <Link to={accountInCommandCenter(accountId)} className={LINK_CLASS}>
            Open account
          </Link>
        )}
        {target.kind === 'contact' && accountId && (
          <Link to={contactInCommandCenter(accountId, target.id)} className={LINK_CLASS}>
            Open contact
          </Link>
        )}
      </div>
    </div>
  );
}

